import { ComponentProps, FC, useMemo } from 'react';

import cn from 'classnames';
import { TiThList } from 'react-icons/ti';
import { IoGrid } from 'react-icons/io5';
import { Button, Tooltip } from 'ui/components/common';
import { FilterForm } from 'ui/components/form';
import { ExportIcon, ImportIcon, SettingIcon } from 'ui/components/icon';
import { useGetSubscription } from 'modules/account/settings/hooks';

import NavAction from '../NavAction';
import { NavOption } from '../NavAction/NavAction';

type PROPS = {
  onClickSettings?: () => void;
  onClickCreate?: () => void;
  onClickExport?: () => void;
  onClickImport?: () => void;
  onToggleView?: (val: any) => void; // TODO: Type this properly
  onFilterChange: (filters: any) => void;
  isBoardView?: boolean;
  type:
    | 'flag'
    | 'booking'
    | 'invoice'
    | 'task'
    | 'deal'
    | 'account'
    | 'account user'
    | 'role'
    | 'product'
    | 'app version'
    | 'automation'
    | 'tag'
    | 'ticket';
  filters: { [key: string]: any };
  disableCreate?: boolean;
  children: ComponentProps<typeof FilterForm>['children'];
  className?: string;
  settingItems?: NavOption[];
  createTooltip?: string | JSX.Element;
  createDataTut?: string;
  settingsDataTut?: string;
};

const PageHeader: FC<PROPS> = ({
  createDataTut,
  settingsDataTut,
  onClickSettings,
  onFilterChange,
  filters,
  type,
  onClickCreate,
  onClickExport,
  onClickImport,
  onToggleView,
  isBoardView = false,
  children,
  className,
  settingItems,
  createTooltip,
  disableCreate,
}) => {
  const { data: subscription } = useGetSubscription();

  const renderButton = useMemo(() => {
    return (
      <Button
        intent="primary"
        className="font-semibold"
        size="sm"
        data-tut={createDataTut}
        label={`CREATE ${type.toUpperCase()}`}
        onClick={onClickCreate}
        disabled={disableCreate || subscription?.isExpired}
        expiredSubscription={subscription?.isExpired}
      />
    );
  }, [createDataTut, disableCreate, onClickCreate, subscription, type]);

  const renderExportButton = useMemo(() => {
    return (
      <Tooltip theme={'dark'} title="Export">
        <ExportIcon
          role="button"
          className="w-5 h-5 hover:text-primary-base transition text-content"
          onClick={onClickExport}
        />
      </Tooltip>
    );
  }, [onClickExport]);

  const renderImportButton = useMemo(() => {
    return (
      <Tooltip theme={'dark'} title="Import">
        <ImportIcon
          role="button"
          className="w-5 h-5 hover:text-primary-base transition text-content"
          onClick={onClickImport}
        />
      </Tooltip>
    );
  }, [onClickImport]);

  return (
    <div
      className={cn(
        'py-4 px-[22px]',
        'border-b-[0.5px] border-separator-primary',
        'shadow-leapify-sm bg-fill-tertiary',
        'relative rounded-t-[6px]',
        className,
      )}
    >
      <FilterForm initialValues={filters} onChange={onFilterChange}>
        {({ values, handleChange }) => children({ values, handleChange })}
      </FilterForm>
      <div
        className={cn(
          'min-h-[36px]',
          'absolute right-[22px] top-4',
          'flex items-center gap-x-6',
        )}
      >
        {onToggleView && (
          <div className="h-5">
            <Tooltip
              theme={'dark'}
              title={`${isBoardView ? 'List' : 'Board'} view`}
            >
              <button
                type="button"
                className="h-5"
                onClick={() => onToggleView((prev: any) => !prev)}
              >
                {isBoardView ? (
                  <TiThList className="h-5 w-5 hover:text-primary-base transition text-content" />
                ) : (
                  <IoGrid className="h-5 w-5 hover:text-primary-base transition text-content" />
                )}
              </button>
            </Tooltip>
          </div>
        )}
        {onClickSettings && (
          <Tooltip theme={'dark'} title="Settings">
            <button
              type="button"
              data-tut={settingsDataTut}
              className="h-5"
              onClick={onClickSettings}
            >
              <SettingIcon className="h-5 w-5 hover:text-primary-base transition text-content" />
            </button>
          </Tooltip>
        )}
        {settingItems && (
          <NavAction
            className="flex items-center"
            label={
              <SettingIcon className="h-5 w-5 hover:text-primary-base transition text-content" />
            }
            options={settingItems}
          />
        )}
        {onClickImport && renderImportButton}
        {onClickExport && renderExportButton}
        {onClickCreate &&
          (createTooltip ? (
            <Tooltip theme={'dark'} title={createTooltip}>
              {renderButton}
            </Tooltip>
          ) : (
            renderButton
          ))}
      </div>
    </div>
  );
};

export default PageHeader;
